import { useState, useEffect, useMemo, useRef } from 'react';

import PropTypes from 'prop-types';
import { BsEnvelope, BsPaperclip, BsXLg } from 'react-icons/bs';

// Sentinel recipient value: send to the company's own email address (no contact person).
// The backend resolves the company email from crmCompanyId when crmContactId is null.
const COMPANY_RECIPIENT = '__company__';

import SignatureSelector from '#emailCampaignFeature/components/advanced/SignatureSelector/SignatureSelector';
import { useEmailTemplates } from '#emailCampaignFeature/models/emailTemplates/queries';
import { useAllSignatures } from '#emailCampaignFeature/models/signatures/queries';
import Button from '#globalComponents/Button/Button';
import Select from '#globalComponents/Forms/Select';
import TextInput from '#globalComponents/Forms/TextInput';
import IconButton from '#globalComponents/IconButton/IconButton';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import RichTextEditor from '#globalComponents/RichTextEditor/RichTextEditor';
import { useErrorStore } from '#lib/stores/errorStore';
import { useAllEmailAccounts } from '#settingsFeature/models/emailAccounts/queries';

import { getActivityEmail, previewActivityEmail } from '../../../../api/crmApi';
import { useCreateActivity, useUpdateActivity } from '../../../../models/activities/mutations';

import { ScheduleModeToggle, nowLocal, resolveDueAt } from './shared';

const buildEmailPayload = ({
	isEdit,
	sendMode,
	isImmediate,
	title,
	dueAt,
	selectedTemplateId,
	selectedContactId,
	selectedAccountId,
	emailTemplates,
	companyId,
	dealId,
	channelTypeId,
	initialData,
	signatureId,
	emailSubject,
	emailContent,
	sendFieldsReady,
}) => {
	const resolvedTitle =
		sendMode === 'template'
			? (emailTemplates.find((t) => t.id === selectedTemplateId)?.name ?? 'E-Mail senden')
			: title.trim();
	// Immediate send (template create only): dueAt = now, the scheduler picks it up within seconds.
	const resolvedDueAt = resolveDueAt({ isImmediate, dueAt });
	// The company-email sentinel means "no contact" — the backend falls back to the company email.
	const resolvedContactId = selectedContactId === COMPANY_RECIPIENT ? null : selectedContactId || null;

	// Subject/content/signature travel only when the editable preview is active — the backend
	// hydrates the template itself otherwise (and PATCHing them requires a still-scheduled send).
	const sendFields = sendFieldsReady
		? {
				signatureId: signatureId ?? null,
				emailSubject,
				emailContent,
			}
		: {};

	if (isEdit) {
		const payload = { title: resolvedTitle, dueAt: resolvedDueAt, crmContactId: resolvedContactId };
		if (sendMode === 'template') {
			payload.emailTemplateId = selectedTemplateId;
			Object.assign(payload, sendFields);
			if (sendFieldsReady && selectedAccountId) payload.emailAccountId = selectedAccountId;
		}
		return { id: initialData.id, payload };
	}

	const base = {
		crmCompanyId: companyId,
		...(dealId && { dealId }),
		channelTypeId: channelTypeId ?? null,
	};

	if (sendMode === 'template') {
		return {
			...base,
			activityType: 'communication',
			title: resolvedTitle,
			status: 'planned',
			dueAt: resolvedDueAt,
			emailTemplateId: selectedTemplateId,
			emailAccountId: selectedAccountId || null,
			crmContactId: resolvedContactId,
			// Omitted entirely when the user made no signature choice yet → backend uses the org default.
			...(signatureId !== undefined && { signatureId: signatureId ?? null }),
			...sendFields,
		};
	}

	return { ...base, activityType: 'task', title: resolvedTitle, status: 'planned', dueAt: resolvedDueAt };
};

/**
 * Resolves the submit button label for the current mode.
 * @param {{isEdit: boolean, sendMode: string, isImmediate: boolean}} state
 * @returns {string}
 */
const submitLabel = ({ isEdit, sendMode, isImmediate }) => {
	if (isEdit) return 'Speichern';
	if (sendMode !== 'template') return 'To-Do anlegen';
	return isImmediate ? 'Jetzt senden' : 'Planen';
};

/**
 * Determines whether the submit button is disabled for the current form state.
 * @param {object} state
 * @returns {boolean}
 */
const isSubmitDisabled = ({
	sendMode,
	title,
	selectedTemplateId,
	selectedContactId,
	selectedAccountId,
	isEdit,
	isCompleted,
	isImmediate,
	dueAt,
	sendFieldsReady,
	emailSubject,
	hasContent,
}) => {
	if (sendMode !== 'template') return !title.trim();
	if (!selectedTemplateId || !selectedContactId) return true;
	if (!isEdit && !selectedAccountId) return true;
	if (sendFieldsReady && (!emailSubject.trim() || !hasContent)) return true;
	return !isCompleted && !isImmediate && !dueAt;
};

const EmailTemplateFields = ({
	selectedTemplateId,
	setSelectedTemplateId,
	selectedContactId,
	setSelectedContactId,
	selectedAccountId,
	setSelectedAccountId,
	emailTemplates,
	emailContacts,
	emailAccounts,
	companyEmail,
}) => (
	<>
		<Select value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
			<option value="">Template wählen…</option>
			{emailTemplates.map((tpl) => (
				<option key={tpl.id} value={tpl.id}>
					{tpl.name}
				</option>
			))}
		</Select>
		{emailTemplates.length === 0 && (
			<p className="text-[11px] text-bgBase-800">
				Noch keine Templates.{' '}
				<a href="/email-campaigns/templates" className="text-primary-600">
					Jetzt erstellen →
				</a>
			</p>
		)}
		{emailAccounts.length > 0 ? (
			<Select value={selectedAccountId} onChange={(e) => setSelectedAccountId(e.target.value)}>
				<option value="">E-Mail-Konto wählen…</option>
				{emailAccounts.map((acc) => (
					<option key={acc.id} value={acc.id}>
						{'Absender: ' + acc.emailAddress}
						{acc.senderName ? ` (${acc.senderName})` : ''}
					</option>
				))}
			</Select>
		) : (
			<p className="text-[11px] text-caution-700">
				Kein verbundenes E-Mail-Konto.{' '}
				<a href="/settings?tab=smtp" className="text-primary-600">
					Jetzt verbinden →
				</a>
			</p>
		)}
		{emailContacts.length > 0 || companyEmail ? (
			<Select value={selectedContactId} onChange={(e) => setSelectedContactId(e.target.value)}>
				<option value="">Empfänger wählen…</option>
				{emailContacts.map((c) => (
					<option key={c.id} value={c.id}>
						{c.fullName ?? (`${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || c.emailAddress)}
						{c.emailAddress ? ` (${c.emailAddress})` : ''}
					</option>
				))}
				{companyEmail && <option value={COMPANY_RECIPIENT}>Firma ({companyEmail})</option>}
			</Select>
		) : (
			<p className="text-[11px] text-caution-700">
				Kein Kontakt mit E-Mail-Adresse und keine Firmen-E-Mail vorhanden.
			</p>
		)}
	</>
);

EmailTemplateFields.propTypes = {
	selectedTemplateId: PropTypes.string.isRequired,
	setSelectedTemplateId: PropTypes.func.isRequired,
	selectedContactId: PropTypes.string.isRequired,
	setSelectedContactId: PropTypes.func.isRequired,
	selectedAccountId: PropTypes.string.isRequired,
	setSelectedAccountId: PropTypes.func.isRequired,
	emailTemplates: PropTypes.array.isRequired,
	emailContacts: PropTypes.array.isRequired,
	emailAccounts: PropTypes.array.isRequired,
	companyEmail: PropTypes.string,
};

const EmailForm = ({ companyId, dealId, contacts = [], onClose, initialData, channelTypeId, companyEmail }) => {
	const isEdit = Boolean(initialData);
	const [sendMode, setSendMode] = useState(() =>
		isEdit ? (initialData.emailTemplateId ? 'template' : 'manual') : 'template',
	);
	const [title, setTitle] = useState(initialData?.title ?? '');
	// 'now' sends immediately (dueAt = now, picked up by the scheduler within seconds);
	// 'later' schedules for the chosen dueAt. Only relevant when creating a template send.
	const [scheduleMode, setScheduleMode] = useState('now');
	const [dueAt, setDueAt] = useState(
		initialData?.dueAt ? new Date(initialData.dueAt).toISOString().slice(0, 16) : '',
	);
	const [selectedTemplateId, setSelectedTemplateId] = useState(initialData?.emailTemplateId ?? '');
	// An automated email activity without a contact sends to the company email — reflect that in
	// the dropdown so the auto-select below does not silently switch the recipient on edit.
	const [selectedContactId, setSelectedContactId] = useState(() =>
		initialData ? (initialData.crmContactId ?? (initialData.emailTemplateId ? COMPANY_RECIPIENT : '')) : '',
	);
	const [selectedAccountId, setSelectedAccountId] = useState('');
	const createActivity = useCreateActivity();
	const updateActivity = useUpdateActivity();
	const { data: emailTemplates = [] } = useEmailTemplates();
	const { data: allEmailAccounts = [] } = useAllEmailAccounts();
	const { data: signatures = [] } = useAllSignatures();
	const showError = useErrorStore((s) => s.showError);

	// Editable preview state. `signatureId === undefined` means "no choice yet" (org default applies).
	const [signatureId, setSignatureId] = useState(undefined);
	const [emailSubject, setEmailSubject] = useState('');
	const [hasContent, setHasContent] = useState(false);
	const [previewData, setPreviewData] = useState(null);
	const [previewState, setPreviewState] = useState('idle');
	const [editor, setEditor] = useState(null);
	const editorRef = useRef(null);
	// Key (templateId|recipient) whose resolved content currently seeds the editor. In edit mode it
	// starts at the stored combination — that content comes from the send snapshot, not the template.
	const seededKeyRef = useRef(
		isEdit && initialData?.emailTemplateId
			? `${initialData.emailTemplateId}|${initialData.crmContactId ?? COMPANY_RECIPIENT}`
			: null,
	);
	// Send fields are only PATCHable while the send is still scheduled.
	const [sendEditable, setSendEditable] = useState(!isEdit);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const emailContacts = useMemo(() => contacts.filter((c) => c.emailAddress), [contacts]);
	const emailAccounts = useMemo(
		() => allEmailAccounts.filter((acc) => acc.connectionStatus === 'connected'),
		[allEmailAccounts],
	);

	useEffect(() => {
		if (selectedContactId) return;
		if (emailContacts.length > 0) {
			const primary = emailContacts.find((c) => c.isPrimary);
			setSelectedContactId(primary?.id ?? (emailContacts.length === 1 ? emailContacts[0].id : ''));
		} else if (companyEmail) {
			// No contact has an email — default to the company's own email address.
			setSelectedContactId(COMPANY_RECIPIENT);
		}
	}, [emailContacts, selectedContactId, companyEmail]);

	useEffect(() => {
		if (isEdit || selectedAccountId || emailAccounts.length === 0) return;
		setSelectedAccountId(emailAccounts[0].id);
	}, [isEdit, emailAccounts, selectedAccountId]);

	// Preselect the org default signature once signatures are loaded (create flow only —
	// edit gets its signature from the send snapshot).
	useEffect(() => {
		if (isEdit || signatureId !== undefined || signatures.length === 0) return;
		setSignatureId(signatures.find((s) => s.isDefault)?.id ?? null);
	}, [isEdit, signatureId, signatures]);

	// Edit prefill: load the scheduled send snapshot (subject/content/signature/account).
	useEffect(() => {
		if (!isEdit || !initialData?.emailSendId) return;
		let alive = true;
		getActivityEmail(initialData.id)
			.then(({ emailSend }) => {
				if (!alive) return;
				setPreviewData({ emailSubject: emailSend.emailSubject, emailContent: emailSend.emailContent });
				setSignatureId(emailSend.signatureId ?? null);
				if (emailSend.emailAccountId) setSelectedAccountId(emailSend.emailAccountId);
				setSendEditable(emailSend.status === 'scheduled');
				setPreviewState('ready');
			})
			.catch(() => {
				if (alive) setPreviewState('error');
			});
		return () => {
			alive = false;
		};
	}, [isEdit, initialData?.emailSendId, initialData?.id]);

	// Resolve the template for the chosen recipient whenever the combination changes. The seeded-key
	// guard keeps refetches (and the edit-mode initial snapshot) from clobbering user edits.
	useEffect(() => {
		if (sendMode !== 'template' || !selectedTemplateId || !selectedContactId) return;
		if (isEdit && !sendEditable) return;
		const key = `${selectedTemplateId}|${selectedContactId}`;
		if (seededKeyRef.current === key) return;
		seededKeyRef.current = key;
		setPreviewState('loading');
		previewActivityEmail({
			emailTemplateId: selectedTemplateId,
			crmContactId: selectedContactId === COMPANY_RECIPIENT ? null : selectedContactId,
			crmCompanyId: companyId ?? null,
		})
			.then((data) => {
				if (seededKeyRef.current !== key) return;
				setPreviewData({
					emailSubject: data.emailSubject,
					emailContent: data.emailContent,
					missingVariables: data.missingVariables ?? [],
				});
				setPreviewState('ready');
			})
			.catch(() => {
				if (seededKeyRef.current === key) setPreviewState('error');
			});
	}, [sendMode, selectedTemplateId, selectedContactId, companyId, isEdit, sendEditable]);

	// Seed subject + editor whenever freshly resolved content arrives (needs the editor mounted).
	useEffect(() => {
		if (!editor || editor.isDestroyed || !previewData) return;
		setEmailSubject(previewData.emailSubject ?? '');
		editor.commands.setContent(previewData.emailContent ?? '');
		setHasContent(Boolean(editor.getText().trim()));
	}, [editor, previewData]);

	const isPending = isEdit ? updateActivity.isPending : createActivity.isPending;
	const isCompleted = initialData?.status === 'completed';
	const isImmediate = !isEdit && sendMode === 'template' && scheduleMode === 'now';
	const showPreview = sendMode === 'template' && selectedTemplateId && selectedContactId && sendEditable;
	const sendFieldsReady = showPreview && previewState === 'ready';

	const recipientEmail =
		selectedContactId === COMPANY_RECIPIENT
			? companyEmail
			: emailContacts.find((c) => c.id === selectedContactId)?.emailAddress;
	const templateAttachment = emailTemplates.find((t) => t.id === selectedTemplateId)?.attachment ?? null;

	const submitActivity = async () => {
		const built = buildEmailPayload({
			isEdit,
			sendMode,
			isImmediate,
			title,
			dueAt,
			selectedTemplateId,
			selectedContactId,
			selectedAccountId,
			emailTemplates,
			companyId,
			dealId,
			channelTypeId,
			initialData,
			signatureId,
			emailSubject: emailSubject.trim(),
			emailContent: editorRef.current?.getHTML() || '',
			sendFieldsReady,
		});
		if (isEdit) {
			await updateActivity.mutateAsync(built);
		} else {
			await createActivity.mutateAsync(built);
		}
		onClose();
	};

	const handleSubmit = async () => {
		if (sendMode === 'template' && (!selectedTemplateId || (!isEdit && !selectedAccountId))) return;
		if (sendMode === 'manual' && !title.trim()) return;

		// Immediate sends are irreversible — reconfirm before the mail leaves.
		if (isImmediate) {
			setConfirmOpen(true);
			return;
		}

		try {
			await submitActivity();
		} catch (err) {
			showError({ error: err, title: isEdit ? 'Speichern fehlgeschlagen' : 'Fehler beim Speichern' });
		}
	};

	const handleConfirmSend = async () => {
		try {
			await submitActivity();
		} catch (err) {
			showError({ error: err, title: 'Fehler beim Senden' });
			// Rethrow so the modal stays open on failure (it closes itself on resolve).
			throw err;
		}
	};

	return (
		<div className="p-3 space-y-2.5">
			<div className="flex items-center gap-2">
				<div className="flex items-center gap-1.5 px-2 py-1 rounded-control bg-primary-100 text-primary-600 text-xs font-medium">
					<BsEnvelope className="w-4 h-4" /> E-Mail
				</div>
				<IconButton icon={BsXLg} label="Schließen" onClick={onClose} className="ml-auto h-6 w-6" />
			</div>

			{!isEdit && (
				<div className="flex gap-1.5">
					<button
						type="button"
						onClick={() => setSendMode('template')}
						className={`flex-1 text-xs px-2.5 py-2 rounded-control border transition-colors ${sendMode === 'template' ? 'border-primary-300 bg-primary-100 text-primary-700 font-medium' : 'border-bgBase-500 text-bgBase-800 hover:border-bgBase-600'}`}
					>
						Mit Template versenden
					</button>
					<button
						type="button"
						onClick={() => setSendMode('manual')}
						className={`flex-1 text-xs px-2.5 py-2 rounded-control border transition-colors ${sendMode === 'manual' ? 'border-primary-300 bg-primary-100 text-primary-700 font-medium' : 'border-bgBase-500 text-bgBase-800 hover:border-bgBase-600'}`}
					>
						Manuell erledigen
					</button>
				</div>
			)}

			{sendMode === 'template' ? (
				<EmailTemplateFields
					selectedTemplateId={selectedTemplateId}
					setSelectedTemplateId={setSelectedTemplateId}
					selectedContactId={selectedContactId}
					setSelectedContactId={setSelectedContactId}
					selectedAccountId={selectedAccountId}
					setSelectedAccountId={setSelectedAccountId}
					emailTemplates={emailTemplates}
					emailContacts={emailContacts}
					emailAccounts={emailAccounts}
					companyEmail={companyEmail}
				/>
			) : (
				<TextInput
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					placeholder="z.B. Follow-up E-Mail schreiben"
					autoFocus
				/>
			)}

			{isEdit && sendMode === 'template' && !sendEditable && (
				<p className="text-[11px] text-bgBase-800">
					Die E-Mail wurde bereits versendet — der Inhalt kann nicht mehr bearbeitet werden.
				</p>
			)}

			{showPreview && (
				<div className="space-y-2.5 border-t border-bgBase-400 pt-2.5">
					{previewState === 'loading' && (
						<p className="text-[11px] text-bgBase-800">Vorschau wird geladen…</p>
					)}
					{previewState === 'error' && (
						<p className="text-[11px] text-caution-700">
							Vorschau konnte nicht geladen werden. Die E-Mail wird beim Versand aus dem Template
							erstellt.
						</p>
					)}
					{previewState === 'ready' && (
						<>
							{previewData?.missingVariables?.length > 0 && (
								<p className="text-[11px] text-caution-700">
									Für diesen Empfänger fehlen Werte für{' '}
									{previewData.missingVariables.map((variable) => variable.name).join(', ')}. Die
									Stellen sind leer – bitte im Text ergänzen.
								</p>
							)}
							<div>
								<label className="block text-[11px] font-medium text-bgBase-800 mb-1">Betreff</label>
								<TextInput value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
							</div>
							<div>
								<label className="block text-[11px] font-medium text-bgBase-800 mb-1">Nachricht</label>
								<RichTextEditor
									minHeightClass="min-h-[220px]"
									editorRef={editorRef}
									onEditorReady={setEditor}
									onUpdate={(_html, text) => setHasContent(Boolean(text.trim()))}
								/>
							</div>
							<div>
								<label className="block text-[11px] font-medium text-bgBase-800 mb-1">Signatur</label>
								<SignatureSelector value={signatureId ?? null} onChange={setSignatureId} />
							</div>
							{templateAttachment && (
								<p className="flex items-center gap-1.5 text-[11px] text-bgBase-800">
									<BsPaperclip className="w-3.5 h-3.5 shrink-0" />
									<span className="truncate">
										{templateAttachment.originalFilename ?? 'Anhang'} wird automatisch angehängt
									</span>
								</p>
							)}
						</>
					)}
				</div>
			)}

			{!isEdit && sendMode === 'template' && (
				<ScheduleModeToggle value={scheduleMode} onChange={setScheduleMode} />
			)}

			{!isCompleted && !isImmediate && (
				<div>
					<TextInput
						type="datetime-local"
						value={dueAt}
						onChange={(e) => setDueAt(e.target.value)}
						min={nowLocal()}
					/>
					{sendMode === 'template' && (
						<p className="text-[10px] text-bgBase-800 mt-0.5">Zeitpunkt für automatischen Versand</p>
					)}
				</div>
			)}

			<div className="flex justify-end gap-2 pt-1">
				<Button type="button" variant="outline" size="sm" onClick={onClose}>
					Abbrechen
				</Button>
				<Button
					variant="solid"
					size="sm"
					onClick={handleSubmit}
					loading={isPending}
					disabled={isSubmitDisabled({
						sendMode,
						title,
						selectedTemplateId,
						selectedContactId,
						selectedAccountId,
						isEdit,
						isCompleted,
						isImmediate,
						dueAt,
						sendFieldsReady,
						emailSubject,
						hasContent,
					})}
				>
					{submitLabel({ isEdit, sendMode, isImmediate })}
				</Button>
			</div>

			<ConfirmModal
				isOpen={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				onConfirm={handleConfirmSend}
				title="E-Mail jetzt senden?"
				message={
					recipientEmail ? (
						<>
							Die E-Mail wird sofort an <strong>{recipientEmail}</strong> gesendet.
						</>
					) : (
						'Die E-Mail wird sofort gesendet.'
					)
				}
				subtitle="Der Versand kann nicht rückgängig gemacht werden."
				confirmText="Jetzt senden"
				color="primary"
				icon={BsEnvelope}
				isLoading={isPending}
			/>
		</div>
	);
};
EmailForm.propTypes = {
	companyId: PropTypes.string,
	dealId: PropTypes.string,
	contacts: PropTypes.array,
	onClose: PropTypes.func,
	initialData: PropTypes.object,
	channelTypeId: PropTypes.string,
	companyEmail: PropTypes.string,
};

export default EmailForm;
